// The report half of the Apple 2703(d) Parser: everything shown once a return has been parsed.
// Kept apart from the upload page so the Case Analyzer desktop app can show a return it has
// already read with exactly the same views. It passes its own top-bar brand, a Close button and
// a document opener; IP screening asks through OnlineGate (see analysis/ui.js) before it
// downloads its public blocklists.

import React, { useState, useMemo, useCallback } from 'react';
import {
  C, FONT, TIME_ZONES, useTimeFormat, kb, plural, NavBtn, StatCard, InfoCard, InfoRow, Heading, Note, btn, select, DataTable,
  GeneratedLegend, useOnlineGate,
} from './ui';
import { ipSummary, timeline, handleSummary, deviceSummary, modelName } from '../../utils/apple2703d';
import {
  buildEvents, patternOfLife, activityGaps, ipProfile, homeAndWork, ipv6Networks, contactProfile, accountLinks,
  deviceTimeline, securityEvents,
} from '../../utils/apple2703dAnalytics';
import {
  PatternOfLifeView, IncidentView, HomeWorkCards, IpScreening, screeningLabel, screeningFlagged, AccountLinksView, SecurityView,
  CaseSummaryView, downloadCaseSummary,
} from './Analysis';

export const CAVEAT = (
  <><strong>Always refer to the actual returns from Apple.</strong> This parser only helps you review the records. It does not
  replace the original data, and anything you rely on (in a report, affidavit or court) should be checked against the original files.</>
);

// Plain-language "What this means" for each kind of sheet, drawn from Apple's
// own notes and Data Guide but written for someone reading a return for the first time.
const EXPLAIN = {
  account: [
    'Who the Apple account belongs to, as the user entered it when signing up: name, address, phone numbers and email addresses.',
    'Apple doesn’t verify names or addresses, so treat them as leads. Verified phone numbers and email addresses were confirmed by the user with a code, which makes them stronger.',
    'The creation dates and Registration IP Address show when and from what network the account was set up. "DS ID" is Apple’s internal account number; the other sheets use it to identify the same account.',
  ],
  icloudServices: [
    'Which iCloud features were switched on for this account when Apple pulled the data (Yes = on).',
    'Features marked * can be end-to-end encrypted when the user has Advanced Data Protection turned on. In that case Apple cannot hand over their contents, even with a warrant.',
  ],
  icloudLogs: [
    'Each row is a moment one of the account’s devices or apps connected to an iCloud service (Mail, Notes, Calendars, Backup, Photos…) and the IP address it connected from.',
    'Useful for showing when the account was in use and which networks it used. The device column often shows the model and iOS version. "null" means Apple didn’t record a device for that connection.',
  ],
  imessageQuery: [
    'When a phone number or email is typed or shown in Messages, Contacts, Phone or Mail, the device quietly asks Apple whether it can receive iMessage. Each row is one of those checks.',
    '"Source handle" is this account doing the checking; "lookup handle" is the number or email being checked. This is not proof that a message was sent: checks also happen automatically, for example when a contact is opened. Numbers checked over and over still tend to be people the user is in touch with.',
    '"Linked User" ties the row to the account’s DSID or registered phone number.',
  ],
  facetimeQuery: [
    'The FaceTime version of the iMessage check: the device asked Apple whether a number or email can take a FaceTime call. "Lookup handle" is who was checked.',
    'It does not show that a call happened. Repeated checks of the same handle are worth a closer look.',
  ],
  facetimeInvites: [
    'Each row is a FaceTime call invitation that passed through Apple: who called whom, when, and from what IP address. The event type codes are listed in Apple’s notes below.',
    'Apple doesn’t know whether a call connected or how long it lasted. An empty sheet means no invitations were logged in the period searched.',
  ],
  mailLogs: [
    'Activity on the account’s iCloud Mail, such as messages sent or received, with the time, IP address, and the sender and recipient in "Event Info".',
    'This is the envelope only: no subject or message content. Times on this sheet are in UTC.',
  ],
  mailMetadata: [
    'One row per email in the iCloud mailbox: when, which mailbox folder, size and Message-ID. No content. The Message-ID matches the mail header files under iCloud Mail.',
  ],
  accountKey: [
    'The list of DSIDs (Apple account numbers) this production covers. The sheets in Apple’s data workbook say "DSID: See Accounts Key" instead of naming one account, so each row is matched to its account by the DSID or person_id column. This tool does that for you.',
  ],
  registrations: [
    'Devices tied to the account and when they were registered. "Registration source" says how Apple learned about the device (for example "icloud" means the device signed in to iCloud).',
    '"date activation_unbrick" is when the device was activated with a carrier. Serial number and IMEI identify the physical phone, which is useful for carrier records or matching a seized device.',
    'Names, emails and addresses here are whatever the customer entered.',
  ],
  devices: [
    'Devices Apple has on file for the account in GCRM, Apple’s customer and support records: serial, IMEI/MEID, product and the contact details attached to it.',
    'The customer can be the buyer, the user or whoever called Apple Support about the device. Contact details are user-entered and not checked by Apple.',
    'Purchase date may really be the first activation date (reseller purchases) or the ship/pickup date (Apple online store). A DSID or email starting or ending with "-" means the account was deleted or deactivated.',
  ],
  subscriber: [
    'The account’s most recent billing profile for Apple Media Services (App Store, Apple Music, Apple TV, iCloud+ and so on): name, billing address and phone, and the payment method’s name.',
    'The billing name can differ from the account holder when someone else’s card is used, which can lead to another person. billing_profile_create_ip is the IP address the billing profile was set up from.',
  ],
  transactions: [
    'Purchases and subscriptions charged to the account: what, when, how much, the card (type, bank, last 4 digits, issuing country) and the IP address used.',
    'A "-" amount is a refund. Subscription renewals copy the IP address from the previous renewal, so the same IP repeating on renewals doesn’t mean the user was on that network then. Only purchases the user actually made reset it.',
    'The card’s bank and last four digits are a route to bank records. global_unique_id identifies the device the purchase was made from.',
  ],
  downloads: [
    'Apps and media downloaded, re-downloaded or updated by the account, with the device model, time and IP address.',
    'This shows which apps were on the user’s devices and when they were active. global_unique_id identifies the device (see Devices → Device IDs).',
  ],
  signOn: [
    'Sign-ins to the Apple account (IdMS is Apple’s sign-in system) for iCloud, the App Store, Game Center and other Apple services: when, which service ("app_name") and from what IP address.',
    'failed_signon_ind: 0 = signed in successfully, 1 = the sign-in failed. A device ID is only recorded for App Store/iTunes sign-ins.',
  ],
  appleIdActivity: [
    'Activity on Apple’s account-management and password-reset sites (My Apple Account and iForgot), such as password resets and account changes, with the IP address, device name and the device’s language and time zone.',
    'A person_id of -1 or -1000 means the account entered was invalid. The device name is whatever the user called their device, which can identify it.',
  ],
  gameCenter: [
    'Connections to Game Center (Apple’s gaming service): when, for how long (session_length_secs), from what IP address and on what device.',
    'unique_device_id identifies the device. Contact details are the user-entered ones on the account.',
  ],
  other: [
    'Records Apple included in this production. See Apple’s notes below and hover the ⓘ next to a column name for Apple’s description of it.',
  ],
};

// Opens a production PDF in a new tab, from memory.
async function openInNewTab(r) {
  const w = window.open('', '_blank');
  const url = URL.createObjectURL(new Blob([await r.buffer()], { type: 'application/pdf' }));
  if (w) w.location.href = url; else window.location.href = url;
}

export default function Apple2703dReport({
  model, onReset, resetLabel = '← New files', brand = null, initialAccount = 'all', openDocument = openInNewTab,
}) {
  const gate = useOnlineGate();
  const [section, setSection] = useState('overview');
  const [tz, setTz] = useState('local');
  const [acct, setAcct] = useState(initialAccount);
  const [showEmptyCols, setShowEmptyCols] = useState(false);
  const [showEmptyFields, setShowEmptyFields] = useState(false);
  const [mailSel, setMailSel] = useState(null);
  const [tlSource, setTlSource] = useState('all');
  const [copied, setCopied] = useState('');
  const [error, setError] = useState('');
  const fmt = useTimeFormat(tz);

  // ── Derived data (all hooks before any early return) ──
  const inAcct = useMemo(() => (acct === 'all' ? () => true : a => a === acct), [acct]);
  const ips = useMemo(() => (model ? ipSummary(model) : []), [model]);
  const events = useMemo(() => (model ? timeline(model) : []), [model]);
  const handles = useMemo(() => (model ? handleSummary(model) : []), [model]);
  const devices = useMemo(() => (model ? deviceSummary(model) : { serials: [], models: [], guids: [] }), [model]);

  // ── Analysis ──
  const allEvents = useMemo(() => (model ? buildEvents(model) : []), [model]);
  const acctEvents = useMemo(() => allEvents.filter(e => inAcct(e.account)), [allEvents, inAcct]);
  const ipProf = useMemo(() => ipProfile(acctEvents, tz), [acctEvents, tz]);
  const ipProfByIp = useMemo(() => new Map(ipProf.map(p => [p.ip.toLowerCase(), p])), [ipProf]);
  const homeWork = useMemo(() => homeAndWork(ipProf), [ipProf]);
  const networks = useMemo(() => ipv6Networks(ipProf), [ipProf]);
  const contacts = useMemo(() => (model ? contactProfile(acctEvents, model.accounts, tz) : []), [model, acctEvents, tz]);
  const deviceTl = useMemo(() => deviceTimeline(acctEvents), [acctEvents]);
  const security = useMemo(() => (model ? securityEvents(model, allEvents).filter(e => inAcct(e.account)) : []), [model, allEvents, inAcct]);
  const links = useMemo(() => {
    if (!model) return [];
    return accountLinks({
      accounts: model.accounts, production: model.production, devices,
      ipProf: ipProfile(allEvents, 'UTC'), contacts: contactProfile(allEvents, model.accounts),
    }).filter(l => acct === 'all' || l.a === acct || l.b === acct);
  }, [model, allEvents, devices, acct]);
  const [screenOn, setScreenOn] = useState(false);
  const [screening, setScreening] = useState({});
  const [screenDone, setScreenDone] = useState(false);
  const onScreened = useCallback((map, done) => { setScreening(map); setScreenDone(done); }, []);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [contactSince, setContactSince] = useState('');
  const [hideOwn, setHideOwn] = useState(true);

  const view = useMemo(() => {
    if (!model) return null;
    const datasets = model.datasets.filter(d => acct === 'all' || d.account === acct || d.rowAccounts.some(inAcct));
    return {
      datasets,
      accounts: model.accounts.filter(a => acct === 'all' || a.key === acct),
      ips: ips.filter(r => r.accounts.some(inAcct)),
      events: events.filter(e => inAcct(e.account)),
      handles: handles.filter(h => h.accounts.some(inAcct)),
      serials: devices.serials.filter(s => s.accounts.some(inAcct)),
      models: devices.models.filter(s => s.accounts.some(inAcct)),
      guids: devices.guids.filter(s => s.accounts.some(inAcct)),
      mail: model.mail.filter(m => inAcct(m.account)),
      summaries: model.mailSummaries.filter(s => inAcct(s.folder || model.production)),
    };
  }, [model, acct, inAcct, ips, events, handles, devices]);
  const screenIps = useMemo(() => (view ? view.ips.map(r => r.ip) : []), [view]);

  // ── Report view ──
  const { datasets, accounts } = view;
  const accountLabel = key => (key === model.production ? 'Apple production' : key);
  const timeCol = (label = 'Time') => ({
    key: 't', label: `${label} (${TIME_ZONES.find(z => z[0] === tz)[1]})`, mono: true, width: 190, generated: true,
    text: r => (r.dateOnly ? r.raw : fmt(r.t)), sort: r => r.t,
  });
  const range = view.events.length ? [view.events[0].t, view.events[view.events.length - 1].t] : null;
  const totalRows = datasets.reduce((n, d) => n + d.rows.filter((_, i) => inAcct(d.rowAccounts[i])).length, 0);
  const recordCount = d => d.rows.filter((_, i) => inAcct(d.rowAccounts[i])).length;
  const go = s => { setSection(s); setMailSel(null); };

  // Group datasets in the sidebar by account.
  const groups = [];
  for (const d of datasets) {
    let g = groups.find(x => x.account === d.account);
    if (!g) groups.push(g = { account: d.account, items: [] });
    g.items.push(d);
  }

  function renderDataset(d) {
    const rows = d.rows.map((r, i) => ({ r, i })).filter(x => inAcct(d.rowAccounts[x.i]));
    const empty = d.header.map((_, c) => rows.every(x => !x.r[c]));
    const hidden = empty.filter(Boolean).length;
    const cols = [];
    if (d.timeCol >= 0) cols.push({ ...timeCol(), text: x => { const t = d.times[x.i]; return !t ? '' : t.dateOnly ? x.r[t.col] : fmt(t.t); }, sort: x => d.times[x.i]?.t });
    if (d.account === model.production && acct === 'all') cols.push({ key: '_acct', label: 'Account', generated: true, title: 'Matched by this row\'s DSID / person_id', text: x => accountLabel(d.rowAccounts[x.i]) });
    d.header.forEach((h, c) => {
      if (empty[c] && !showEmptyCols) return;
      cols.push({ key: `c${c}`, label: h, title: d.descriptions[h.toLowerCase()], mono: d.ipCols.includes(c), text: x => x.r[c] });
    });
    return (
      <>
        <Heading title={d.label} sub={`${accountLabel(d.account)} · ${d.fileName}${d.sheetName ? ` › ${d.sheetName}` : ''}`}>
          {hidden > 0 && (
            <label style={{ fontSize: 12, color: C.muted, display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" checked={showEmptyCols} onChange={e => setShowEmptyCols(e.target.checked)} />
              Show {plural(hidden, 'empty column')}
            </label>
          )}
        </Heading>
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 12.5, color: C.muted }}>
          {d.meta.title && d.meta.title !== d.label && <span>{d.meta.title}</span>}
          {(d.meta.dsid || d.meta.identifier) && <span>DSID: <span style={{ color: C.text }}>{d.meta.dsid || d.meta.identifier}</span></span>}
          {d.meta.phone && <span>Phone: <span style={{ color: C.text }}>{d.meta.phone}</span></span>}
          {d.meta.period && <span>Period: <span style={{ color: C.text }}>{d.meta.period}</span></span>}
          {d.meta.timezone && <span>Times as provided: <span style={{ color: C.text }}>{d.meta.timezone}</span></span>}
        </div>
        <div style={{ background: 'rgba(59,130,246,.08)', border: '1px solid rgba(59,130,246,.35)', borderRadius: 8, padding: '12px 16px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: C.accentSoft, marginBottom: 6 }}>What this means</div>
          {(EXPLAIN[d.kind] || EXPLAIN.other).map((p, i) => (
            <p key={i} style={{ margin: i ? '6px 0 0' : 0, fontSize: 13, lineHeight: 1.6, color: C.text }}>{p}</p>
          ))}
          {d.timeCol >= 0 && d.meta.timezone && (
            <p style={{ margin: '6px 0 0', fontSize: 13, lineHeight: 1.6, color: C.text }}>
              Apple gives these times in {d.meta.timezone}. The first column converts them to the time zone picked at the top; the original value is kept in its own column.
            </p>
          )}
        </div>
        {d.assumedUtc && <Note tone="warn">Some times in this sheet don&rsquo;t say their time zone, so they&rsquo;re treated as UTC.</Note>}
        {d.notes.length > 0 && (
          <details style={{ fontSize: 12.5, color: C.muted }}>
            <summary style={{ cursor: 'pointer' }}>Apple&rsquo;s notes on this sheet ({d.notes.length})</summary>
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6, lineHeight: 1.6 }}>
              {d.notes.map((n, i) => <div key={i}>{n}</div>)}
            </div>
          </details>
        )}
        {d.header.length ? (
          <DataTable key={d.id + showEmptyCols + tz} columns={cols} rows={rows}
            exportName={`${accountLabel(d.account)} ${d.label}`}
            initialSort={d.timeCol >= 0 ? { key: 't', dir: 'asc' } : null}
            emptyText="No records in this sheet for the period Apple searched." />
        ) : <Note>No table in this sheet.</Note>}
        <div style={{ fontSize: 11.5, color: C.muted }}>
          Review aid only. Always refer to the original file from Apple: {d.fileName}{d.sheetName ? `, sheet "${d.sheetName}"` : ''}.
          {' '}Columns marked <span style={{ color: C.warn }}>✱</span> (converted times, matched accounts) are generated by LE Tech Tools and are not part of the original legal return.
        </div>
      </>
    );
  }

  function renderAccount(a) {
    const kinds = {};
    model.datasets.forEach(d => d.rows.forEach((_, i) => {
      if (d.rowAccounts[i] === a.key) kinds[d.label] = (kinds[d.label] || 0) + 1;
    }));
    const fields = (a.profile || []).filter(([, v]) => showEmptyFields || v);
    return (
      <div key={a.key} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 2fr)', gap: 16 }}>
        <InfoCard title={a.name ? `${a.name} · ${accountLabel(a.key)}` : accountLabel(a.key)}
          action={a.profile && (
            <label style={{ fontSize: 11.5, color: C.muted, display: 'flex', gap: 5, alignItems: 'center' }}>
              <input type="checkbox" checked={showEmptyFields} onChange={e => setShowEmptyFields(e.target.checked)} /> Empty fields
            </label>
          )}>
          {fields.length ? fields.map(([k, v], i) => <InfoRow key={k} label={k} value={v || '—'} first={i === 0} mono={/ip|id$|phone|dsid/i.test(k)} />) : (
            <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
              No Account Details sheet for this account.
              {a.dsids.length > 0 && <div>DSID: <span style={{ color: C.text, fontFamily: 'monospace' }}>{a.dsids.join(', ')}</span></div>}
              {a.phones.length > 0 && <div>Phone: <span style={{ color: C.text, fontFamily: 'monospace' }}>{a.phones.join(', ')}</span></div>}
            </div>
          )}
        </InfoCard>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
          {a.services.length > 0 && (
            <InfoCard title="iCloud services">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {a.services.map(([name, on]) => (
                  <span key={name} title={on} style={{
                    fontSize: 12, padding: '3px 9px', borderRadius: 12,
                    background: /^yes|^on|^true|enabled/i.test(on) ? 'rgba(134,239,172,.14)' : 'rgba(148,163,184,.10)',
                    color: /^yes|^on|^true|enabled/i.test(on) ? C.good : C.muted, border: `1px solid ${C.border}`,
                  }}>{name}{/^no|^off|^false|disabled/i.test(on) ? ' (off)' : ''}</span>
                ))}
              </div>
            </InfoCard>
          )}
          <InfoCard title="Records for this account ✱">
            {Object.keys(kinds).length ? Object.entries(kinds).sort((x, y) => y[1] - x[1]).map(([k, n], i) => (
              <InfoRow key={k} label={k} value={n.toLocaleString()} first={i === 0} />
            )) : <div style={{ fontSize: 13, color: C.muted }}>No records.</div>}
          </InfoCard>
        </div>
      </div>
    );
  }

  const ipCols = [
    { key: 'ip', label: 'IP address', mono: true, color: C.cyan },
    { key: 'v', label: 'Type', text: r => (r.ip.includes(':') ? 'IPv6' : 'IPv4'), width: 60 },
    { key: 'count', label: 'Times seen', sort: r => r.count, width: 90 },
    { key: 'first', label: `First seen (${TIME_ZONES.find(z => z[0] === tz)[1]})`, mono: true, text: r => fmt(r.first), sort: r => r.first },
    { key: 'last', label: 'Last seen', mono: true, text: r => fmt(r.last), sort: r => r.last },
    { generated: true, key: 'days', label: 'Days', title: 'Different days this IP was used for activity', width: 50, text: r => ipProfByIp.get(r.ip.toLowerCase())?.days ?? '', sort: r => ipProfByIp.get(r.ip.toLowerCase())?.days ?? 0 },
    { generated: true, key: 'nights', label: 'Nights', title: 'Different nights (22:00–06:00) this IP was used', width: 55, text: r => ipProfByIp.get(r.ip.toLowerCase())?.nights ?? '', sort: r => ipProfByIp.get(r.ip.toLowerCase())?.nights ?? 0 },
    { generated: true, key: 'workdays', label: 'Workdays', title: 'Different weekdays this IP was used 09:00–17:00', width: 70, text: r => ipProfByIp.get(r.ip.toLowerCase())?.workdays ?? '', sort: r => ipProfByIp.get(r.ip.toLowerCase())?.workdays ?? 0 },
    ...(screenOn ? [{ generated: true, key: 'screen', label: 'Screening', text: r => screeningLabel(screening[r.ip.toLowerCase()]),
      color: undefined, render: r => {
        const res = screening[r.ip.toLowerCase()];
        return <span style={{ color: screeningFlagged(res) ? C.warn : C.muted }}>{screeningLabel(res)}</span>;
      } }] : []),
    { key: 'sources', label: 'Seen in', text: r => r.sources.join(', ') },
    { key: 'accounts', label: 'Accounts', text: r => r.accounts.map(accountLabel).join(', ') },
  ];
  const tzLabel = TIME_ZONES.find(z => z[0] === tz)[1];
  const sinceT = contactSince ? Date.parse(contactSince + 'T00:00:00Z') : null;
  const contactRows = contacts.filter(c => (!hideOwn || !c.own) && (sinceT == null || c.first >= sinceT));
  const targetCount = model.accounts.filter(a => a.key !== model.production).length;
  async function makePdf() {
    setPdfBusy(true);
    try {
      const pol = patternOfLife(acctEvents, tz);
      await downloadCaseSummary({
        scope: acct === 'all' ? 'All accounts' : accountLabel(acct), fmt, tzLabel, accountLabel,
        accounts: accounts.filter(a => a.key !== model.production || a.profile),
        homeWork, pol, gaps: activityGaps(acctEvents, 12).sort((a, b) => b.hours - a.hours),
        ips: [...ipProf].sort((a, b) => b.days - a.days || b.count - a.count),
        screen: ip => (screenOn ? screeningLabel(screening[ip.toLowerCase()]) : 'Not screened'),
        contacts: [...contacts].filter(c => !c.own).sort((a, b) => b.days - a.days || b.count - a.count),
        serials: view.serials, deviceTl, security, links,
        files: [...new Set(model.files.filter(f => f.status === 'parsed').map(f => f.path))],
      });
    } catch (err) {
      setError('Could not build the PDF: ' + (err?.message || String(err)));
    } finally {
      setPdfBusy(false);
    }
  }
  const sources = [...new Set(view.events.map(e => e.source))].sort();
  const tlRows = tlSource === 'all' ? view.events : view.events.filter(e => e.source === tlSource);
  const queryDatasets = datasets.filter(d => ['imessageQuery', 'facetimeQuery', 'facetimeInvites'].includes(d.kind));
  const mailDatasets = datasets.filter(d => ['mailLogs', 'mailMetadata'].includes(d.kind));
  const selectedMail = mailSel != null ? view.mail.find(m => m.path === mailSel) : null;

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: C.bg, color: C.text, fontFamily: FONT }}>
      {/* Top bar */}
      <div style={{ minHeight: 58, background: '#0a0f1e', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', padding: '8px 24px', gap: 12, flexShrink: 0, flexWrap: 'wrap' }}>
        {brand || (
          <>
            <a href="/" style={{ fontSize: 17, fontWeight: 800, color: '#fff', letterSpacing: '-0.3px', textDecoration: 'none' }}>LE Tech Tools</a>
            <span style={{ color: C.border, fontSize: 18 }}>/</span>
            <span style={{ fontSize: 15, fontWeight: 600, color: C.accentSoft }}>Apple Subpoena & 2703(d) Parser</span>
          </>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12, color: C.muted }}>Account{' '}
            <select value={acct} onChange={e => setAcct(e.target.value)} style={select}>
              <option value="all">All accounts ({model.accounts.length})</option>
              {model.accounts.map(a => <option key={a.key} value={a.key}>{a.name ? `${a.name} · ` : ''}{accountLabel(a.key)}</option>)}
            </select>
          </label>
          <label style={{ fontSize: 12, color: C.muted }}>Show times in{' '}
            <select value={tz} onChange={e => setTz(e.target.value)} style={select}>
              {TIME_ZONES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <button onClick={onReset} style={{ ...btn, color: C.muted, background: 'none' }}>{resetLabel}</button>
        </div>
      </div>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar */}
        <aside style={{ width: 250, background: C.surface, borderRight: `1px solid ${C.border}`, overflowY: 'auto', flexShrink: 0, padding: '8px 0' }}>
          <NavBtn label="Overview" active={section === 'overview'} onClick={() => go('overview')} />
          <NavBtn label="Accounts" badge={accounts.length} active={section === 'accounts'} onClick={() => go('accounts')} />
          <NavBtn label="Timeline" badge={view.events.length.toLocaleString()} active={section === 'timeline'} onClick={() => go('timeline')} />
          <NavBtn label="IP addresses" badge={view.ips.length.toLocaleString()} active={section === 'ips'} onClick={() => go('ips')} />
          <NavBtn label="iMessage & FaceTime" badge={view.handles.length.toLocaleString()} active={section === 'handles'} onClick={() => go('handles')} />
          <NavBtn label="iCloud Mail" badge={view.mail.length || undefined} active={section === 'mail'} onClick={() => go('mail')} />
          <NavBtn label="Devices" badge={(view.serials.length + view.models.length + view.guids.length) || undefined} active={section === 'devices'} onClick={() => go('devices')} />
          <div style={{ height: 1, background: C.border, margin: '8px 12px' }} />
          <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted, padding: '6px 18px' }}>Analysis</div>
          <NavBtn label="Pattern of life" active={section === 'pol'} onClick={() => go('pol')} />
          <NavBtn label="Incident window" active={section === 'incident'} onClick={() => go('incident')} />
          <NavBtn label="Links between accounts" badge={links.length || undefined} active={section === 'links'} onClick={() => go('links')} />
          <NavBtn label="Security events" badge={security.length || undefined} active={section === 'security'} onClick={() => go('security')} />
          <NavBtn label="Case summary (PDF)" active={section === 'summary'} onClick={() => go('summary')} />
          <div style={{ height: 1, background: C.border, margin: '8px 12px' }} />
          <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted, padding: '6px 18px' }}>All records</div>
          {groups.map(g => (
            <div key={g.account}>
              <div style={{ fontSize: 11.5, color: C.text, padding: '8px 18px 3px', fontWeight: 600, wordBreak: 'break-all' }}>{accountLabel(g.account)}</div>
              {g.items.map(d => (
                <NavBtn key={d.id} indent label={d.label} badge={recordCount(d).toLocaleString()} active={section === d.id} onClick={() => go(d.id)} />
              ))}
            </div>
          ))}
          <div style={{ height: 1, background: C.border, margin: '8px 12px' }} />
          <NavBtn label="Documents" badge={model.documents.length || undefined} active={section === 'documents'} onClick={() => go('documents')} />
          <NavBtn label="Files read" badge={model.files.length} active={section === 'files'} onClick={() => go('files')} />
        </aside>

        {/* Content */}
        <main style={{ flex: 1, overflowY: 'auto', padding: '22px 28px 40px', display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>

          {section === 'overview' && (
            <>
              <Heading title="Overview" sub={range ? `Activity from ${fmt(range[0])} to ${fmt(range[1])}` : undefined} />
              <GeneratedLegend />
              <Note tone="warn">{CAVEAT}</Note>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
                <StatCard number={accounts.filter(a => a.key !== model.production).length} label="Accounts" />
                <StatCard number={totalRows.toLocaleString()} label="Records" />
                <StatCard number={view.events.length.toLocaleString()} label="Timeline events" />
                <StatCard number={view.ips.length.toLocaleString()} label="Unique IP addresses" />
                <StatCard number={view.handles.length.toLocaleString()} label="Handles queried" />
                <StatCard number={view.serials.length + view.guids.length} label="Devices (serials & IDs)" />
              </div>
              {model.warnings.length > 0 && (
                <Note tone="warn">{model.warnings.slice(0, 6).map((w, i) => <div key={i}>{w}</div>)}{model.warnings.length > 6 && <div>…and {model.warnings.length - 6} more (see Files read).</div>}</Note>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
                {accounts.filter(a => a.key !== model.production || a.datasets.length).map(a => {
                  const get = k => ((a.profile || []).find(([h]) => h.toLowerCase() === k.toLowerCase()) || [])[1];
                  const rows = [
                    ['Name', a.name], ['Apple ID', a.appleId || a.emails[0]], ['DSID', a.dsids.join(', ')],
                    ['Phone', get('FaceTime / iMessage Phone') || get('Verified Phone') || a.phones.join(', ')],
                    ['Status', get('Account Status')], ['Created', get('Apple ID Creation Date')], ['Account type', get('Account Type')],
                  ].filter(([, v]) => v);
                  return (
                    <InfoCard key={a.key} title={accountLabel(a.key)}
                      action={<button style={btn} onClick={() => { setAcct(a.key); go('accounts'); }}>Open</button>}>
                      {rows.length ? rows.map(([k, v], i) => <InfoRow key={k} label={k} value={v} first={i === 0} mono={/DSID|Phone/.test(k)} />)
                        : <div style={{ fontSize: 13, color: C.muted }}>{plural(a.datasets.reduce((n, d) => n + d.rows.length, 0), 'record')}</div>}
                    </InfoCard>
                  );
                })}
              </div>
              {view.ips.length > 0 && (
                <InfoCard title="Most frequent IP addresses ✱" action={<button style={btn} onClick={() => go('ips')}>All IPs</button>}>
                  {[...view.ips].sort((a, b) => b.count - a.count).slice(0, 10).map((r, i, arr) => (
                    <div key={r.ip} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '5px 0', borderTop: i ? `1px solid ${C.border}` : 'none', fontSize: 13 }}>
                      <div style={{ height: 6, background: C.accent, borderRadius: 3, width: Math.max(4, Math.round(r.count / arr[0].count * 80)), flexShrink: 0 }} />
                      <span style={{ fontFamily: 'monospace', color: C.cyan, wordBreak: 'break-all' }}>{r.ip}</span>
                      <span style={{ marginLeft: 'auto', color: C.muted, whiteSpace: 'nowrap' }}>{r.count.toLocaleString()}</span>
                    </div>
                  ))}
                </InfoCard>
              )}
              {model.documents.length > 0 && (
                <Note>Apple&rsquo;s production also includes {plural(model.documents.length, 'PDF')} (the legal request, court order, business-record declaration and read-me). See <button style={{ ...btn, padding: '1px 6px' }} onClick={() => go('documents')}>Documents</button>.</Note>
              )}
            </>
          )}

          {section === 'accounts' && (
            <>
              <Heading title="Accounts" sub="Subscriber details from each account's Account Details sheet, plus its iCloud services." />
              {accounts.map(renderAccount)}
            </>
          )}

          {section === 'timeline' && (
            <>
              <Heading title="Timeline" sub="Every dated record from every sheet, in one list.">
                <select value={tlSource} onChange={e => setTlSource(e.target.value)} style={select}>
                  <option value="all">All sources</option>
                  {sources.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </Heading>
              <GeneratedLegend />
              <DataTable key={tz + tlSource + acct} rows={tlRows} exportName="Apple 2703d timeline" initialSort={{ key: 't', dir: 'asc' }}
                columns={[
                  timeCol(),
                  { key: 'account', label: 'Account', text: r => accountLabel(r.account) },
                  { key: 'source', label: 'Source' },
                  { key: 'detail', label: 'Details', maxWidth: 700, minWidth: 360 },
                ]} />
            </>
          )}

          {section === 'ips' && (
            <>
              <Heading title="IP addresses" sub="Every IP address in every sheet and mail header, with when and where it was seen.">
                <button style={btn} onClick={() => {
                  navigator.clipboard?.writeText(view.ips.map(r => r.ip).join('\n')).then(() => { setCopied('ips'); setTimeout(() => setCopied(''), 1500); }, () => {});
                }}>{copied === 'ips' ? 'Copied' : 'Copy all IPs'}</button>
              </Heading>
              <GeneratedLegend />
              <HomeWorkCards homeWork={homeWork} fmt={fmt} profile={ipProf} />
              <Note>
                &ldquo;Likely home&rdquo; is the IP or IPv6 network used on the most different nights (22:00–06:00), and &ldquo;likely
                work&rdquo; the one used on the most weekdays 09:00–17:00, both in the time zone chosen at the top. They&rsquo;re leads,
                not facts: confirm them with the ISP. Phone carrier IPs change often, so a home network usually shows up as Wi-Fi.
              </Note>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                {!screenOn ? (
                  <button style={btn} onClick={() => gate.ask('ip-lists').then(ok => ok && setScreenOn(true))}>Screen these IPs for VPN, Tor, iCloud Private Relay and hosting</button>
                ) : <IpScreening ips={screenIps} onResults={onScreened} />}
                {gate.note || (
                  <span style={{ fontSize: 12, color: C.muted }}>
                    Uses the same blocklists as the IP and Domain Intelligence tool, checked in your browser. The lists are downloaded the
                    first time (or reused from that tool); the IP addresses themselves are never sent anywhere.
                  </span>
                )}
              </div>
              {screenOn && screenDone && (() => {
                const flagged = view.ips.filter(r => screeningFlagged(screening[r.ip.toLowerCase()]));
                return flagged.length ? <Note tone="warn">{plural(flagged.length, 'IP')} flagged as VPN, Tor, iCloud Private Relay, hosting or on abuse lists. Their location is not where the person was.</Note>
                  : <Note>No VPN, Tor, Private Relay or hosting IPs found.</Note>;
              })()}
              <DataTable key={tz + acct + screenOn} rows={view.ips} columns={ipCols} exportName="Apple 2703d IP addresses" initialSort={{ key: 'count', dir: 'desc' }} />
              {networks.length > 0 && (
                <>
                  <Heading generated title="IPv6 networks" sub="IPv6 addresses grouped by their first 64 bits: addresses in one group came through the same home, office or carrier network." />
                  <DataTable key={'n' + tz + acct} rows={networks} exportName="Apple 2703d IPv6 networks" initialSort={{ key: 'count', dir: 'desc' }}
                    columns={[
                      { key: 'network', label: 'Network', mono: true, color: C.cyan },
                      { key: 'addresses', label: 'Addresses', width: 80 },
                      { key: 'count', label: 'Records', width: 80 },
                      { key: 'nights', label: 'Nights', width: 60 },
                      { key: 'first', label: `First (${tzLabel})`, mono: true, text: r => fmt(r.first), sort: r => r.first },
                      { key: 'last', label: 'Last', mono: true, text: r => fmt(r.last), sort: r => r.last },
                      { key: 'accounts', label: 'Accounts', text: r => r.accounts.map(accountLabel).join(', ') },
                    ]} />
                </>
              )}
            </>
          )}

          {section === 'handles' && (
            <>
              <Heading title="iMessage & FaceTime" sub="Phone numbers and emails the account's devices looked up, from Apple's capability query and call invitation logs." />
              <GeneratedLegend />
              <Note>
                Apple&rsquo;s own caveat: a capability query means a device asked Apple whether a handle supports iMessage or FaceTime
                (for example when a contact was viewed or typed in). It does not show that a message was sent or a call was made.
              </Note>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', fontSize: 12.5, color: C.muted }}>
                <label>First looked up on or after{' '}
                  <input type="date" value={contactSince} onChange={e => setContactSince(e.target.value)} style={{ ...select, colorScheme: 'dark' }} />
                </label>
                {contactSince && <button style={btn} onClick={() => setContactSince('')}>Clear date</button>}
                <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="checkbox" checked={hideOwn} onChange={e => setHideOwn(e.target.checked)} /> Hide the account&rsquo;s own numbers and emails
                </label>
              </div>
              <Note>
                <span style={{ color: C.warn }}>✱</span> Ranked by <strong style={{ color: C.text }}>days</strong>: how many different days a handle was looked up. Phones look
                up contacts automatically, so raw counts are inflated by bursts; a number looked up on many separate days is much more
                likely to be someone the user is actually in touch with. Use the date filter to find people who first appear after a
                given day (for example, after the incident). &ldquo;Shared&rdquo; means more than one account in this return looked it up.
              </Note>
              <DataTable key={tz + acct + contactSince + hideOwn} rows={contactRows} exportName="Apple 2703d contacts" initialSort={{ key: 'days', dir: 'desc' }}
                emptyText="No iMessage or FaceTime lookups match."
                columns={[
                  { key: 'handle', label: 'Handle', mono: true, color: C.cyan },
                  { key: 'type', label: 'Type', width: 60, text: r => (/@/.test(r.handle) ? 'Email' : /^\+?\d[\d_ ()-]*$/.test(r.handle) ? 'Phone' : 'Other') },
                  { key: 'days', label: 'Days', width: 50, generated: true, title: 'Different days this handle was looked up', sort: r => r.days },
                  { key: 'count', label: 'Lookups', width: 70, sort: r => r.count },
                  { key: 'first', label: `First (${tzLabel})`, mono: true, text: r => fmt(r.first), sort: r => r.first },
                  { key: 'last', label: 'Last', mono: true, text: r => fmt(r.last), sort: r => r.last },
                  { key: 'services', label: 'Service', text: r => r.services.join(', ') },
                  { key: 'accounts', label: 'Looked up by', text: r => r.accounts.map(accountLabel).join(', ') },
                  { key: 'notes', label: 'Notes', generated: true, text: r => [r.own && 'own handle', r.accounts.filter(a => a !== model.production).length > 1 && 'shared'].filter(Boolean).join(', ') },
                ]} />
              {queryDatasets.length > 0 && (
                <InfoCard title="Source sheets">
                  {queryDatasets.map((d, i) => (
                    <InfoRow key={d.id} first={i === 0} label={`${d.label}`}
                      value={<button style={{ ...btn, padding: '2px 8px' }} onClick={() => go(d.id)}>{accountLabel(d.account)} · {plural(recordCount(d), 'row')}</button>} />
                  ))}
                </InfoCard>
              )}
            </>
          )}

          {section === 'mail' && (
            <>
              <Heading title="iCloud Mail" sub="Mail header files, mail item records and the summary Apple includes for each account." />
              {view.summaries.map(s => (
                <Note key={s.path}><strong style={{ color: C.text }}>{accountLabel(s.folder || model.production)}:</strong> {s.text.split(/\r?\n/).filter(Boolean).join(' · ')}</Note>
              ))}
              {view.mail.length > 0 && (
                <DataTable key={tz + acct} rows={view.mail} exportName="Apple 2703d mail headers" initialSort={{ key: 't', dir: 'asc' }}
                  onRowClick={m => setMailSel(m.path === mailSel ? null : m.path)} rowKey={m => m.path} selectedKey={mailSel}
                  columns={[
                    { key: 't', label: `Date (${TIME_ZONES.find(z => z[0] === tz)[1]})`, mono: true, generated: true, text: m => (m.time ? fmt(m.time.t) : m.date), sort: m => m.time?.t },
                    { key: 'from', label: 'From' }, { key: 'to', label: 'To' }, { key: 'subject', label: 'Subject' },
                    { key: 'account', label: 'Account', text: m => accountLabel(m.account) },
                  ]} />
              )}
              {selectedMail && (
                <InfoCard title={`Headers · ${selectedMail.name}`} action={<button style={btn} onClick={() => setMailSel(null)}>Close</button>}>
                  {selectedMail.headers.map(([k, v], i) => <InfoRow key={i} label={k} value={v} first={i === 0} mono />)}
                </InfoCard>
              )}
              {mailDatasets.length > 0 && (
                <InfoCard title="Mail sheets">
                  {mailDatasets.map((d, i) => (
                    <InfoRow key={d.id} first={i === 0} label={d.label}
                      value={<button style={{ ...btn, padding: '2px 8px' }} onClick={() => go(d.id)}>{accountLabel(d.account)} · {plural(recordCount(d), 'row')}</button>} />
                  ))}
                </InfoCard>
              )}
              {!view.mail.length && !mailDatasets.length && !view.summaries.length && <Note>No iCloud Mail data.</Note>}
            </>
          )}

          {section === 'devices' && (
            <>
              <Heading title="Devices" sub="Devices by serial number (GCRM records, registrations and IDS logs), device IDs from purchases, sign-ons and Game Center, and device models seen in the logs." />
              <GeneratedLegend />
              <div style={{ fontSize: 13, fontWeight: 600 }}>By serial number</div>
              <DataTable key={'s' + acct} rows={view.serials} exportName="Apple 2703d devices by serial" initialSort={{ key: 'count', dir: 'desc' }}
                emptyText="No serial numbers in this return."
                columns={[
                  { key: 'serial', label: 'Serial', mono: true, color: C.cyan },
                  { key: 'imei', label: 'IMEI', mono: true, text: r => r.imei.join(', ') },
                  { key: 'meid', label: 'MEID', mono: true, text: r => r.meid.join(', ') },
                  { key: 'description', label: 'Product', text: r => r.description.join(', ') },
                  { key: 'count', label: 'Records', sort: r => r.count, width: 80 },
                  { key: 'sources', label: 'Seen in', text: r => r.sources.join(', ') },
                  { key: 'accounts', label: 'Accounts', text: r => r.accounts.map(accountLabel).join(', ') },
                ]} />
              <div style={{ fontSize: 13, fontWeight: 600, marginTop: 8 }}>Device IDs (GUID / UDID)</div>
              <Note>Apple&rsquo;s unique device identifier, recorded on purchases, downloads, App Store sign-ins and Game Center. The same ID in several places is the same physical device.</Note>
              <DataTable key={'g' + acct + tz} rows={view.guids} exportName="Apple 2703d device IDs" initialSort={{ key: 'count', dir: 'desc' }}
                emptyText="No device IDs in this return."
                columns={[
                  { key: 'guid', label: 'Device ID', mono: true, color: C.cyan },
                  { key: 'models', label: 'Model', text: r => r.models.map(m => modelName(m) ? `${m} (${modelName(m)})` : m).join(', ') },
                  { key: 'count', label: 'Records', sort: r => r.count, width: 80 },
                  { key: 'first', label: `First (${TIME_ZONES.find(z => z[0] === tz)[1]})`, mono: true, text: r => fmt(r.first), sort: r => r.first },
                  { key: 'last', label: 'Last', mono: true, text: r => fmt(r.last), sort: r => r.last },
                  { key: 'sources', label: 'Seen in', text: r => r.sources.join(', ') },
                  { key: 'accounts', label: 'Accounts', text: r => r.accounts.map(accountLabel).join(', ') },
                ]} />
              <Heading generated title="Device timeline" sub="Each phone model and iOS version in the logs, with when it first and last appears. A new model appearing usually means a new phone; a new iOS version is a software update." />
              <DataTable key={'tl' + acct + tz} rows={deviceTl} exportName="Apple 2703d device timeline" initialSort={{ key: 'first', dir: 'asc' }}
                emptyText="No device models or iOS versions in the logs."
                columns={[
                  { key: 'model', label: 'Model', mono: true, text: r => r.model || '(not logged)' },
                  { key: 'name', label: 'Name', generated: true, text: r => r.name || '' },
                  { key: 'os', label: 'iOS', text: r => r.os },
                  { key: 'first', label: `First seen (${tzLabel})`, mono: true, text: r => fmt(r.first), sort: r => r.first },
                  { key: 'last', label: 'Last seen', mono: true, text: r => fmt(r.last), sort: r => r.last },
                  { key: 'count', label: 'Records', width: 70, sort: r => r.count },
                  { key: 'accounts', label: 'Accounts', text: r => r.accounts.map(accountLabel).join(', ') },
                ]} />
              <div style={{ fontSize: 13, fontWeight: 600, marginTop: 8 }}>Device models in the logs</div>
              <DataTable key={'m' + acct} rows={view.models} exportName="Apple 2703d device models" initialSort={{ key: 'count', dir: 'desc' }}
                emptyText="No device models in the logs."
                columns={[
                  { key: 'id', label: 'Model identifier', mono: true },
                  { key: 'name', label: 'Model', generated: true, title: 'Marketing name looked up from the model identifier', text: r => r.name || modelName(r.id) || '—' },
                  { key: 'count', label: 'Log entries', sort: r => r.count, width: 100 },
                  { key: 'sources', label: 'Seen in', text: r => r.sources.join(', ') },
                  { key: 'accounts', label: 'Accounts', text: r => r.accounts.map(accountLabel).join(', ') },
                ]} />
            </>
          )}

          {section === 'documents' && (
            <>
              <Heading title="Documents" sub="PDFs in the production, opened in a new tab from memory (nothing is uploaded)." />
              <DataTable rows={model.documents} columns={[
                { key: 'name', label: 'File' },
                { key: 'path', label: 'Path', maxWidth: 520 },
                { key: 'size', label: 'Size', text: r => kb(r.size), sort: r => r.size },
                { key: 'open', label: '', render: r => (
                  <button style={btn} onClick={() => Promise.resolve(openDocument(r)).catch(err => setError('Could not open the document: ' + (err?.message || String(err))))}>Open</button>
                ) },
              ]} />
            </>
          )}

          {section === 'files' && (
            <>
              <Heading title="Files read" sub="Everything in the upload and what the parser did with it." />
              <DataTable rows={model.files} exportName="Apple 2703d files" columns={[
                { key: 'path', label: 'File', maxWidth: 600 },
                { key: 'status', label: 'Result', width: 90, color: undefined },
                { key: 'note', label: 'Note' },
                { key: 'size', label: 'Size', text: r => (r.size ? kb(r.size) : ''), sort: r => r.size },
              ]} />
            </>
          )}

          {section === 'pol' && <PatternOfLifeView events={acctEvents} tz={tz} fmt={fmt} tzLabel={tzLabel} />}
          {section === 'incident' && <IncidentView events={acctEvents} tz={tz} fmt={fmt} tzLabel={tzLabel} accountLabel={accountLabel} homeWork={homeWork} />}
          {section === 'links' && <AccountLinksView links={links} accountLabel={accountLabel} targetCount={targetCount} />}
          {section === 'security' && <SecurityView rows={security} fmt={fmt} tzLabel={tzLabel} accountLabel={accountLabel} />}
          {section === 'summary' && (
            <CaseSummaryView onDownload={makePdf} busy={pdfBusy} scope={acct === 'all' ? 'all accounts' : accountLabel(acct)}
              counts={[
                ['Accounts', accounts.filter(a => a.key !== model.production).length],
                ['IP addresses', `${ipProf.length} (${screenOn ? 'screened' : 'not screened yet: run it on the IP addresses page'})`],
                ['Contacts', contacts.filter(c => !c.own).length],
                ['Devices', `${view.serials.length} serials, ${deviceTl.length} model / iOS rows`],
                ['Security events', security.length],
                ['Links between accounts', links.length],
              ]} />
          )}
          {error && <Note tone="warn">{error}</Note>}

          {datasets.filter(d => d.id === section).map(d => <React.Fragment key={d.id}>{renderDataset(d)}</React.Fragment>)}
        </main>
      </div>
    </div>
  );
}
